import {useEffect, useRef} from "react";
import {useLocation} from "react-router-dom";
import AppProviders from "./AppProviders";
import App from "./App";
import {CookieBanner} from "./consent/CookieBanner";
import {CookieSettingsLauncher} from "./consent/CookieSettingsLauncher";
import {removeStaleHydrationSiblings} from "./utils/staleHydrationTree";
import {useIsomorphicLayoutEffect} from "./utils/useIsomorphicLayoutEffect";
import {installPanelIntentListener} from "./perf/panelLatency";
import {useAnalyticsConsent} from "./analytics/useAnalyticsConsent";
import {Locale} from "./i18n";

/**
 * Root route element rendered around every route (client + pre-rendered). Supplies the global
 * context providers (theme, user, terms, Stripe) and then renders the {@link App} shell, which in
 * turn renders the active route via react-router's `<Outlet/>`.
 *
 * Splitting providers (here) from the shell that consumes them (App) keeps `useUser()` etc. inside
 * their providers. The {@link CookieBanner} is mounted here (inside the providers, so it reads the
 * theme) so the consent gate runs on every page (ui#72), while {@link useAnalyticsConsent} keeps tagging
 * in step with the stored consent for the whole session (ui#256).
 *
 * {@link CookieSettingsLauncher} is its counterpart after the answer (ui#2675): the banner unmounts
 * the moment consent is decided, and GDPR Art. 7(3) wants taking the permission back to be as easy
 * as giving it, so the way back is mounted at the same level and on the same every-page terms.
 *
 * The `display: contents` wrapper gives the whole app exactly ONE top-level DOM node (layout
 * neutral) so the ui#78 cleanup below can drop the stale pre-rendered tree an interrupted
 * hydration leaves behind in `#root` — see {@link removeStaleHydrationSiblings}.
 */
export default function RootLayout({forcedLocale}: {forcedLocale?: Locale} = {}) {
	const appRootRef = useRef<HTMLDivElement>(null);
	// Remove abandoned hydration siblings after commit but before first paint; the isomorphic fallback
	// keeps SSG warning-free, where effects do not run.
	useIsomorphicLayoutEffect(() => {
		const node = appRootRef.current;
		const removed = removeStaleHydrationSiblings(node?.parentElement ?? null, node);
		if (removed > 0) {
			// Signal, not noise: confirms the interrupted-hydration path fired on this load (ui#78).
			console.warn(`[ui#78] removed ${removed} stale pre-rendered tree(s) left by interrupted hydration`);
		}
	}, []);
	// ui#169: arm the panel-open latency mark on any click targeting a feature-panel route. Document-
	// level capture (installed once; effects never run during SSG) keeps NavBar untouched; the
	// listener's returned uninstaller doubles as the effect cleanup.
	useEffect(() => installPanelIntentListener(document), []);
	// ui#256: tagging follows the consent STORE from here, above the banner — the banner unmounts the
	// moment consent is decided, so a later withdrawal must not depend on it still being mounted.
	useAnalyticsConsent();
	// The storefront follows the address (kitecorp/transferify-api-service#2638), so the providers hear about
	// every navigation, not only the ones that switch tree.
	const {pathname} = useLocation();
	return (
		<div data-app-root ref={appRootRef} style={{display: "contents"}}>
			<AppProviders forcedLocale={forcedLocale} pathname={pathname}>
				<App/>
				<CookieBanner/>
				<CookieSettingsLauncher/>
			</AppProviders>
		</div>
	);
}
